<template>
  <component :is="portal" v-bind="teleport ? { to: 'body' } : {}">
    <div
      ref="panel"
      data-cy="select-options-panel"
      class="flex flex-col overflow-hidden bg-white border text-gray-900 border-gray-300 rounded-lg shadow-lg dark:bg-gray-700 dark:border-gray-600"
      :class="
        teleport
          ? ['z-[9999]', panelStyle ? '' : 'invisible']
          : [
              'absolute left-0 right-0 z-[200]',
              placement === 'top' ? 'bottom-full mb-1' : 'mt-1'
            ]
      "
      :style="panelStyle"
    >
      <div
        v-if="filter"
        ref="filterRow"
        class="shrink-0 p-2 border-b border-gray-200 dark:border-gray-600"
      >
        <SelectInputFilter v-model="query" :placeholder="filterPlaceholder" />
      </div>
      <slot></slot>
    </div>
  </component>
</template>

<script lang="ts">
/** Estimated rendered height of the filter row; see rowDrift.ts. */
export const FILTER_ROW_HEIGHT = 57;
</script>

<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  toRef
} from "vue";
import { SafeTeleport } from "vue-safe-teleport";

import { useAnchoredDropdown } from "@/composables/useAnchoredDropdown";
import { warnOnRowDrift } from "./rowDrift";
import SelectInputFilter from "./SelectInputFilter.vue";

// A panel floated beside its anchor, teleported to body or left in the flow,
// that flips above the anchor and caps its height to fit the visible viewport.
// It carries the filter row; the rows below it are slotted in. Mounted only
// while open, so it positions itself from setup and asks to be dismissed when
// the user presses outside it or the anchor scrolls out of view.
const props = withDefaults(
  defineProps<{
    anchor: HTMLElement | null;
    /** Teleport to body for fixed coordinates; false leaves the panel in flow under the anchor. */
    teleport?: boolean;
    /** Height the slotted rows want, so the flip decision can add the filter row on top. */
    contentHeight?: number;
    filter?: boolean;
    filterPlaceholder?: string;
  }>(),
  {
    teleport: true,
    contentHeight: 240,
    filter: false,
    filterPlaceholder: "Filter options..."
  }
);

const emit = defineEmits<{
  (e: "dismiss"): void;
}>();

const query = defineModel<string>("query", { default: "" });

const panel = ref<HTMLElement | null>(null);
const filterRow = ref<HTMLElement | null>(null);

const portal = computed(() => (props.teleport ? SafeTeleport : "div"));

const preferredHeight = computed(
  () => props.contentHeight + (props.filter ? FILTER_ROW_HEIGHT : 0)
);

const { floatingStyle, placement, maxHeight } = useAnchoredDropdown({
  anchor: toRef(props, "anchor"),
  isOpen: ref(true),
  positioned: toRef(props, "teleport"),
  preferredHeight,
  onAnchorHidden: () => emit("dismiss")
});

// A teleported panel gets its whole box from the composable; an in-flow panel
// already sits under the anchor and only needs the height cap. A cap of 0 is
// still a cap — dropping it would let the panel run past the visible area.
const panelStyle = computed(() =>
  props.teleport
    ? floatingStyle.value
    : maxHeight.value !== null
      ? { maxHeight: `${maxHeight.value}px` }
      : null
);

// The click that opened the panel is still bubbling when this listener is
// added, so the anchor has to count as inside.
const onDocumentClick = (event: MouseEvent) => {
  const target = event.target as Node;
  if (props.anchor?.contains(target) || panel.value?.contains(target)) return;
  emit("dismiss");
};

onMounted(() => {
  document.addEventListener("click", onDocumentClick);
  if (import.meta.env.DEV) {
    nextTick(() =>
      warnOnRowDrift(filterRow.value, FILTER_ROW_HEIGHT, "FILTER_ROW_HEIGHT")
    );
  }
});

onBeforeUnmount(() => {
  document.removeEventListener("click", onDocumentClick);
});
</script>
